'use client';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import Section from '@repo/ui/src/components/Section';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { MediaType } from '@repo/ui/src/types/image';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@style/variables';
import { useIsSM, useIsMD, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';

gsap.registerPlugin(ScrollTrigger);

interface AllGrillsHeroProps {
  toutContent: ToutContentProps;
  largeGrillImage: MediaType;
  smallGrillImage: MediaType;
}

const AllGrillsHero = ({ toutContent, largeGrillImage, smallGrillImage }: AllGrillsHeroProps) => {
  const mdAndUp = useIsMD();
  const smAndUp = useIsSM();
  const reduceMotion = useReducedMotion();
  const [imagesLoaded, setImagesLoaded] = useState({
    grill: false,
    smoke1: false,
    smoke2: false,
  });

  const imageSize = smAndUp ? imageFullWidthSmAndUp : imageFullWidthXs;

  const smokeImage = {
    endpoint: 'traeger',
    name: 'transparent-smoke',
  };

  const smokeImgUrl = buildImageSrc(smokeImage, imageSize, 0, 0);

  const grillImgUrl = buildImageSrc(mdAndUp ? largeGrillImage : smallGrillImage, imageSize, 0, 0);

  const fadeDelay = smAndUp ? 5 : 7;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const hero = element.querySelector('.series-hero') as HTMLElement;
    const grillContent = element.querySelector('.grill-hero-content') as HTMLElement;
    const smokeWrapper = element.querySelector('.smoke-wrapper') as HTMLElement;
    const smokeImage1 = element.querySelector('.smoke-image-1 img') as HTMLImageElement;
    const smokeImage2 = element.querySelector('.smoke-image-2 img') as HTMLImageElement;

    const heroGrillTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top center',
        end: 'bottom 40%',
        scrub: 0.5,
      },
    });

    heroGrillTimeline
      .addLabel('start')
      .fromTo(
        grillContent,
        { translateY: reduceMotion ? 0 : 100, opacity: 0 },
        { translateY: 0, opacity: 1, duration: 1 },
      )
      .delay(fadeDelay)
      .addLabel('smoke')
      .fromTo(
        smokeWrapper,
        { yPercent: reduceMotion ? 0 : 100, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 3 },
        'smoke',
      )
      .fromTo(
        smokeImage1,
        { xPercent: reduceMotion ? 0 : -12 },
        { xPercent: 0, duration: 3 },
        'smoke',
      )
      .fromTo(
        smokeImage2,
        { xPercent: reduceMotion ? 0 : 6 },
        { xPercent: 0, duration: 3 },
        'smoke',
      );

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [fadeDelay, reduceMotion]);

  return (
    <section ref={ref} className="grill-hero-content">
      <Section dark texture className="series-hero sm:min-h-[480px] !-z-10">
        <ToutContent
          dark
          divider
          flexible
          {...toutContent}
          headingStyleVariant="h1"
          className="overflow-hidden text-center px-6 sm:pb-[30vh] sm:pt-[14rem] md:pt-[128px] md:pb-[40vh] fixed top-0 left-0 right-0 flex flex-col justify-center h-[70vh] md:h-[100vh]"
        />

        <div className="relative overflow-hidden pt-[55vh]">
          <div className="m-auto w-full px-6 after:absolute after:bottom-[-1px] after:h-[37vw] md:after:h-[24vw] after:z-101 after:bg-fixed after:w-full after:left-0 after:bg-speckle after:bg-[#f6f4f3]">
            <div
              className="z-[102] relative transition-opacity duration-500 ease-in-out"
              style={{
                opacity: imagesLoaded.grill ? 1 : 0,
              }}
            >
              <Image
                src={grillImgUrl}
                alt={largeGrillImage?.name || 'Traeger Grills'}
                width={imageSize}
                height={imageSize}
                className="relative w-full block"
                id="series-grill-image"
                priority={true}
                onLoadingComplete={() => setImagesLoaded((prev) => ({ ...prev, grill: true }))}
              />
            </div>
          </div>
          <div className="smoke-wrapper absolute mb-[20vw] sm:mb-[25vh] left-0 bottom-0 w-full opacity-0 translate-y-full z-[100]">
            <div
              className="smoke-image-1 w-[110%] absolute bottom-0 z-[101] transition-opacity duration-500 ease-in-out"
              style={{
                opacity: imagesLoaded.smoke1 ? 1 : 0,
              }}
            >
              <Image
                src={smokeImgUrl}
                width={imageSize}
                height={imageSize}
                role="presentation"
                alt=""
                priority={true}
                className="absolute w-full left-0 right-auto bottom-0"
                onLoadingComplete={() => setImagesLoaded((prev) => ({ ...prev, smoke1: true }))}
              />
            </div>
            <div
              className="smoke-image-2 z-[101] relative transition-opacity duration-500 ease-in-out"
              style={{
                opacity: imagesLoaded.smoke2 ? 1 : 0,
              }}
            >
              <Image
                src={smokeImgUrl}
                width={imageSize}
                height={imageSize}
                role="presentation"
                alt=""
                className="absolute w-full left-auto right-0 bottom-0 -scale-x-100"
                priority={true}
                onLoadingComplete={() => setImagesLoaded((prev) => ({ ...prev, smoke2: true }))}
              />
            </div>
          </div>
        </div>
      </Section>
    </section>
  );
};

export default AllGrillsHero;
